import type { Map as GlobeMap, ScaleControl } from 'maplibre-gl';
import { t } from './i18n.js';

/** Use the globe camera while retaining the editor's zoom and scale layout. */
export function mountGlobeMapControls(map: GlobeMap, library: { ScaleControl: typeof ScaleControl }): void {
  const row = document.querySelector('.zoom-scale-row');
  if (!row) return;
  const zoom = document.createElement('div');
  zoom.className = 'ol-zoom ol-unselectable ol-control globe-zoom-scale-control';
  const buttons = [
    { label: 'map_controls.zoom_in', text: '+', direction: 1 },
    { label: 'map_controls.zoom_out', text: '−', direction: -1 },
  ] as const;
  const zoomButtons = buttons.map(({ label, text, direction }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = direction > 0 ? 'ol-zoom-in' : 'ol-zoom-out';
    button.id = direction > 0 ? 'globe-zoom-in' : 'globe-zoom-out';
    button.textContent = text;
    button.title = t(label);
    button.setAttribute('aria-label', t(label));
    button.addEventListener('click', () => {
      map.zoomTo(Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), map.getZoom() + direction)), { duration: 250 });
    });
    zoom.appendChild(button);
    return button;
  });
  row.appendChild(zoom);
  const scales = (['metric', 'imperial'] as const).map(unit => {
    const control = new library.ScaleControl({ unit, maxWidth: 100 });
    const wrapper = document.createElement('div');
    wrapper.className = `ol-scale-line ${unit}-scale globe-zoom-scale-control`;
    wrapper.id = `globe-${unit}-scale`;
    // MapLibre measures ground distance across the centre of the current view,
    // including the globe projection and camera tilt, and updates on movement.
    const scale = control.onAdd(map);
    scale.className = `ol-scale-line ${unit}-scale-inner`;
    wrapper.appendChild(scale);
    row.appendChild(wrapper);
    return { control, wrapper };
  });
  const updateZoom = () => {
    zoomButtons[0].disabled = map.getZoom() >= map.getMaxZoom();
    zoomButtons[1].disabled = map.getZoom() <= map.getMinZoom();
  };
  map.on('zoom', updateZoom);
  updateZoom();
  map.once('remove', () => {
    map.off('zoom', updateZoom);
    zoom.remove();
    for (const { control, wrapper } of scales) {
      control.onRemove();
      wrapper.remove();
    }
  });
}
